import { isKnownField } from './fields.js';
import type { Condition, DesignElement, TicketDesign } from './layout.js';
import { parseDesign } from './layout.js';
import { PREVIEW_SCENARIOS } from './scenarios.js';
import { conditionHolds, milsToDots, pointsToMils, textFor } from './resolve.js';

/**
 * What has to be true before a design may be published (D-89, PART 14, 17).
 *
 * The rules that matter are the ones that make a ticket fail at the door: a QR
 * code too small for the printer's dots, a code with something printed across
 * it, a design with no way to identify the ticket at all, a field that no
 * longer exists. Those are **errors** and they block publishing. Things that
 * are merely likely to look wrong — text that will not fit the box on the
 * worst realistic ticket, an element outside the printable area, two elements
 * on top of each other — are **warnings**, because the designer can see the
 * preview and may mean it.
 *
 * Every check is run against every preview scenario, not against one tidy
 * sample: a design is only finished when it survives the long names and the
 * ticket with no seat.
 */

export type IssueSeverity = 'ERROR' | 'WARNING';

export interface DesignIssue {
  severity: IssueSeverity;
  code: string;
  message: string;
  element_id?: string;
  scenario?: string;
}

/** A QR module has to be at least this many printer dots to scan reliably from thermal stock. */
const MIN_QR_MODULE_DOTS = 3;
/** A credential is a long payload; below this the modules get too fine for a 300-dpi head. */
const MIN_QR_SIDE_MILS = 800;
const MIN_BARCODE_HEIGHT_MILS = 250;
const MIN_TEXT_PT = 5;
/** Rough average glyph width as a fraction of the font size, for fit estimates. */
const GLYPH_WIDTH_RATIO = 0.52;

const area = (element: DesignElement) => ({ x1: element.x_mils, y1: element.y_mils, x2: element.x_mils + element.width_mils, y2: element.y_mils + element.height_mils });

const overlaps = (a: DesignElement, b: DesignElement, padding = 0) => {
  const one = area(a);
  const two = area(b);
  return one.x1 - padding < two.x2 && two.x1 - padding < one.x2 && one.y1 - padding < two.y2 && two.y1 - padding < one.y2;
};

/** Every field a condition mentions, so a retired field is caught there too. */
function conditionFields(condition: Condition | null | undefined, out: string[] = []): string[] {
  if (!condition) return out;
  if ('all' in condition) condition.all.forEach((child) => conditionFields(child, out));
  else if ('any' in condition) condition.any.forEach((child) => conditionFields(child, out));
  else if ('not' in condition) conditionFields(condition.not, out);
  else out.push(condition.field);
  return out;
}

/** How many lines this text needs in the box, at this size. */
function linesNeeded(text: string, widthMils: number, sizePt: number): number {
  const charWidth = pointsToMils(sizePt) * GLYPH_WIDTH_RATIO;
  if (charWidth <= 0) return 1;
  const perLine = Math.max(1, Math.floor(widthMils / charWidth));
  let lines = 1;
  let used = 0;
  for (const word of text.split(/\s+/)) {
    const length = word.length + (used > 0 ? 1 : 0);
    if (used + length > perLine && used > 0) {
      lines += 1;
      used = word.length;
    } else {
      used += length;
    }
  }
  return lines;
}

export function validateDesign(input: unknown): { design: TicketDesign; issues: DesignIssue[]; publishable: boolean } {
  const design = parseDesign(input);
  const issues: DesignIssue[] = [];
  const add = (severity: IssueSeverity, code: string, message: string, extra: { element_id?: string; scenario?: string } = {}) => issues.push({ severity, code, message, ...extra });

  const { canvas, elements } = design;
  const printable = {
    x1: canvas.margins.left_mils,
    y1: canvas.margins.top_mils,
    x2: canvas.width_mils - canvas.margins.right_mils,
    y2: canvas.height_mils - canvas.margins.bottom_mils,
  };

  if (elements.length === 0) add('ERROR', 'EMPTY_DESIGN', 'This design has nothing on it.');

  // --- A ticket must be identifiable at the door ------------------------------------------------
  const codes = elements.filter((element) => element.kind === 'QR' || element.kind === 'BARCODE');
  const hasShortCode = elements.some((element) => element.kind === 'FIELD' && element.field === 'ticket.short_code');
  if (codes.length === 0 && !hasShortCode) {
    add('ERROR', 'NOTHING_TO_SCAN', 'Nothing on this ticket identifies it: add a QR code, a barcode, or at least the short code so the door can type it in.');
  }
  if (elements.some((element) => element.kind === 'FIELD' && element.field === 'ticket.credential')) {
    add('ERROR', 'CREDENTIAL_AS_TEXT', 'The credential is printed as text. It is the admission itself — print it as a QR code, never as readable text anyone can copy.');
  }

  for (const element of elements) {
    const label = element.name ?? element.id;

    // --- Inside the paper -----------------------------------------------------------------------
    const box = area(element);
    if (box.x1 < 0 || box.y1 < 0 || box.x2 > canvas.width_mils || box.y2 > canvas.height_mils) {
      add('ERROR', 'OFF_THE_STOCK', `“${label}” runs off the edge of the stock; it will be cut off.`, { element_id: element.id });
    } else if (box.x1 < printable.x1 || box.y1 < printable.y1 || box.x2 > printable.x2 || box.y2 > printable.y2) {
      add('WARNING', 'IN_THE_MARGIN', `“${label}” is inside the margin, where the printer may not reach.`, { element_id: element.id });
    }

    // --- Fields must exist ----------------------------------------------------------------------
    if (element.kind === 'FIELD' && !isKnownField(element.field)) {
      add('ERROR', 'UNKNOWN_FIELD', `“${label}” uses a field that does not exist (${element.field}).`, { element_id: element.id });
    }
    for (const field of conditionFields(element.visible_when)) {
      if (!isKnownField(field)) add('ERROR', 'UNKNOWN_CONDITION_FIELD', `The rule on “${label}” asks about a field that does not exist (${field}).`, { element_id: element.id });
    }

    // --- Readable text --------------------------------------------------------------------------
    if ((element.kind === 'TEXT' || element.kind === 'FIELD') && element.size_pt < MIN_TEXT_PT) {
      add('ERROR', 'TEXT_TOO_SMALL', `“${label}” is set at ${element.size_pt}pt. Anything under ${MIN_TEXT_PT}pt will not print legibly.`, { element_id: element.id });
    }

    // --- Codes that will actually scan (PART 14) ------------------------------------------------
    if (element.kind === 'QR') {
      const side = Math.min(element.width_mils, element.height_mils);
      if (side < MIN_QR_SIDE_MILS && element.source === 'CREDENTIAL') {
        add('ERROR', 'QR_TOO_SMALL', `The QR code “${label}” is ${(side / 1000).toFixed(2)}in. A signed credential needs at least ${(MIN_QR_SIDE_MILS / 1000).toFixed(2)}in to scan from paper.`, {
          element_id: element.id,
        });
      }
      if (canvas.kind === 'BOCA') {
        // A credential is ~45 QR modules across; each needs whole printer dots.
        const modules = element.source === 'CREDENTIAL' ? 45 : 25;
        const moduleDots = milsToDots(side / modules, canvas.dpi ?? 300);
        if (moduleDots < MIN_QR_MODULE_DOTS) {
          add('ERROR', 'QR_MODULE_TOO_FINE', `At ${canvas.dpi ?? 300} dpi, each square of “${label}” would be ${moduleDots} dot${moduleDots === 1 ? '' : 's'} across. Make it bigger, or the scanner will not read it.`, {
            element_id: element.id,
          });
        }
      }
      const quiet = element.quiet_zone_mils ?? 40;
      for (const other of elements) {
        if (other.id === element.id || other.kind === 'BOX') continue;
        if (overlaps(element, other, quiet)) {
          add('ERROR', 'QR_CROWDED', `“${other.name ?? other.id}” is on top of the QR code “${label}”, or inside the blank space it needs. A crowded code does not scan.`, { element_id: element.id });
        }
      }
    }

    if (element.kind === 'BARCODE') {
      if (element.height_mils < MIN_BARCODE_HEIGHT_MILS) {
        add('ERROR', 'BARCODE_TOO_SHORT', `The barcode “${label}” is shorter than ${(MIN_BARCODE_HEIGHT_MILS / 1000).toFixed(2)}in; a hand scanner will struggle.`, { element_id: element.id });
      }
      const quiet = element.quiet_zone_mils ?? 100;
      for (const other of elements) {
        if (other.id === element.id || other.kind === 'BOX') continue;
        if (overlaps(element, other, quiet)) {
          add('ERROR', 'BARCODE_CROWDED', `“${other.name ?? other.id}” is inside the quiet zone of the barcode “${label}”.`, { element_id: element.id });
        }
      }
    }

    // --- What a thermal printer cannot do ---------------------------------------------------------
    if (canvas.kind === 'BOCA') {
      if (element.kind === 'IMAGE' || element.kind === 'SPONSOR') {
        add('WARNING', 'BOCA_IMAGE', `“${label}” is an image. A BOCA prints only logos that were loaded into the printer beforehand; it will be left out unless this one was.`, { element_id: element.id });
      }
      if ((element.kind === 'TEXT' || element.kind === 'FIELD') && element.color_hex && element.color_hex !== '#000000') {
        add('WARNING', 'BOCA_COLOR', `“${label}” is colored. Thermal stock prints black only.`, { element_id: element.id });
      }
      if (element.rotation) {
        add('WARNING', 'BOCA_ROTATION', `“${label}” is rotated. A BOCA rotates in 90-degree steps only, and some models not at all — check the test print.`, { element_id: element.id });
      }
    }
  }

  // --- Two things in one place -------------------------------------------------------------------
  for (let i = 0; i < elements.length; i += 1) {
    for (let j = i + 1; j < elements.length; j += 1) {
      const a = elements[i]!;
      const b = elements[j]!;
      if (a.kind === 'BOX' || b.kind === 'BOX' || a.kind === 'QR' || b.kind === 'QR' || a.kind === 'BARCODE' || b.kind === 'BARCODE') continue;
      // Two things that are never shown together cannot collide.
      const exclusive = PREVIEW_SCENARIOS.every((scenario) => !(conditionHolds(a.visible_when, scenario.values) && conditionHolds(b.visible_when, scenario.values)));
      if (!exclusive && overlaps(a, b)) {
        add('WARNING', 'OVERLAP', `“${a.name ?? a.id}” and “${b.name ?? b.id}” overlap.`, { element_id: a.id });
      }
    }
  }

  // --- Against every realistic ticket --------------------------------------------------------------
  for (const scenario of PREVIEW_SCENARIOS) {
    for (const element of elements) {
      if (element.kind !== 'TEXT' && element.kind !== 'FIELD') continue;
      if (!conditionHolds(element.visible_when, scenario.values)) continue;
      const text = textFor(element, scenario.values);
      if (!text) continue;
      const lines = linesNeeded(text, element.width_mils, element.size_pt);
      const lineHeight = pointsToMils(element.size_pt) * (element.line_height ?? 1.2);
      const allowed = element.max_lines ?? Math.max(1, Math.floor(element.height_mils / lineHeight));
      if (lines > allowed && !element.min_size_pt) {
        add('WARNING', 'TEXT_OVERFLOWS', `“${element.name ?? element.id}” does not fit on ${scenario.name.toLowerCase()} — it needs ${lines} lines and has room for ${allowed}. Give it more room, a smaller size, or let it shrink to fit.`, {
          element_id: element.id,
          scenario: scenario.key,
        });
      }
    }
  }

  return { design, issues, publishable: !issues.some((issue) => issue.severity === 'ERROR') };
}
